import React, { useState, useMemo, useCallback } from 'react';
import { Widget } from '../../api/dashbboards/DashboardWidgets';
import ChartView from '../charts/ChartView';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash } from '@fortawesome/pro-light-svg-icons';
import '../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { library } from '@fortawesome/fontawesome-svg-core';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { fal } from '@fortawesome/pro-light-svg-icons';
import './AutoWidget.scss';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { DashboardFilter } from '../../api/dashbboards/DashboardFilter';
import { Size } from '../../types/databuilder/databuilder';
import TableView from '../data-builder/TableView';
import TextView from './widgets/TextView';
import { TDashboardInightsEntity } from '../../api/shared-entity/SharedEntity';

library.add(far, fas, fal);

type Props = {
	widget: Widget;
	selectedDate?: TNewDateRange;
	removeWidget?: (widget: Widget) => void;
	updateWidget?: (widget: Widget) => void;
	handleEditClick?: (widget: Widget) => void;
	setEditingId?: (id?: number) => void;
	filters?: DashboardFilter[];
	isEdit?: boolean;
	allowClickNavigate?: boolean;
	size?: Size;
	noAnimation?: boolean;
	handleWidgetSave?: (widget: Widget) => void;
	isEmptyDashboardFilters?: boolean;
	isInsightsOpen?: boolean;
	selectedChartWidget?: Widget;
	insightEntities?: TDashboardInightsEntity[];
};

export type TextObjectType = {
	type: 'p' | 'h2' | 'h3';
	value: string;
};

const AutoWidget = (props: Props) => {
	const {
		widget,
		selectedDate,
		removeWidget,
		updateWidget,
		handleEditClick,
		setEditingId,
		filters,
		isEdit,
		size,
		noAnimation,
		allowClickNavigate,
	} = props;
	const [editing, setEditing] = useState<boolean>(false);

	const toggleEdit = useCallback(() => {
		setEditing((prev) => {
			const newState = !prev;
			if (setEditingId) {
				setEditingId(newState ? widget.id : undefined);
			}
			return newState;
		});
	}, [setEditingId, widget.id]);

	const handleRemoveWidget = useCallback(() => {
		removeWidget && widget && removeWidget(widget);
	}, [removeWidget, widget]);

	const handleEditClick_ = useCallback(() => {
		handleEditClick && handleEditClick(widget);
	}, [handleEditClick, widget]);

	const getTextComponent = useMemo(() => {
		return (
			<>
				<TextView
					filters={filters ?? []}
					isEditModalOpen={editing ?? false}
					setIsEditModalOpen={setEditing}
					isEdit={isEdit ?? false}
					toggleEdit={toggleEdit}
					updateWidget={updateWidget}
					widget={widget}
					selectedDate={selectedDate}
					handleWidgetSave={props.handleWidgetSave}
					isEmptyDashboardFilters={props.isEmptyDashboardFilters}
				/>
				<span className="edit-span">
					<FontAwesomeIcon
						icon={faPencil}
						className="edit-icon"
						size="xs"
						onClick={(e) => {
							e.stopPropagation();
							toggleEdit();
						}}
					/>
				</span>
				<span className="delete-span">
					<FontAwesomeIcon
						icon={faTrash}
						className="delete-icon"
						size="xs"
						onClick={(e) => {
							e.stopPropagation();
							handleRemoveWidget();
						}}
					/>
				</span>
			</>
		);
	}, [
		filters,
		editing,
		isEdit,
		toggleEdit,
		updateWidget,
		widget,
		selectedDate,
		props.handleWidgetSave,
		props.isEmptyDashboardFilters,
		handleRemoveWidget,
	]);

	const getChartComponent = useMemo(() => {
		return (
			<>
				<ChartView
					widgetId={widget.id ?? 0}
					chartId={widget.chart?.toString() ?? '0'}
					selectedDate={selectedDate}
					allowClickNavigate={allowClickNavigate}
					filters={filters}
					isEdit={isEdit}
					transparentBackground={true}
					width={size ? size.width : ''}
					height={size ? size.height : ''}
					delayedDisplayTime={1000}
					noAnimation={noAnimation}
					selectedChartWidget={props.selectedChartWidget}
					insightEntities={props.insightEntities}
				/>
				<span className="edit-span">
					<FontAwesomeIcon
						icon={faPencil}
						className="edit-icon"
						size="xs"
						onClick={handleEditClick_}
					/>
				</span>
				<span className="delete-span">
					<FontAwesomeIcon
						icon={faTrash}
						className="delete-icon"
						size="xs"
						onClick={handleRemoveWidget}
					/>
				</span>
			</>
		);
	}, [
		widget.id,
		widget.chart,
		selectedDate,
		allowClickNavigate,
		filters,
		isEdit,
		size,
		noAnimation,
		handleEditClick_,
		handleRemoveWidget,
	]);

	const getPivotTableComponent = useMemo(() => {
		return (
			<>
				<TableView
					widgetId={widget.id ?? 0}
					reportId={widget.report?.toString() ?? '0'}
					filters={filters}
					isEdit={isEdit}
					allowClickNavigate={allowClickNavigate}
					selectedDate={selectedDate}
					delayedDisplayTime={500}
					selectedWidget={props.selectedChartWidget}
				/>
				<span className="edit-span">
					<FontAwesomeIcon
						icon={faPencil}
						className="edit-icon"
						size="xs"
						onClick={handleEditClick_}
					/>
				</span>
				<span className="delete-span">
					<FontAwesomeIcon
						icon={faTrash}
						className="delete-icon"
						size="xs"
						onClick={handleRemoveWidget}
					/>
				</span>
			</>
		);
	}, [
		widget.id,
		widget.report,
		filters,
		isEdit,
		allowClickNavigate,
		selectedDate,
		props.selectedChartWidget,
		handleEditClick_,
		handleRemoveWidget,
	]);

	const component = useMemo(() => {
		switch (widget.widget_type) {
			case 'text':
				return getTextComponent;
			case 'chart':
				return getChartComponent;
			case 'pivot_table':
				return getPivotTableComponent;
			default:
				return <></>;
		}
	}, [widget.widget_type, getTextComponent, getChartComponent, getPivotTableComponent]);

	return <>{component}</>;
};

export default React.memo(AutoWidget);
